import React, { useCallback, useRef, useEffect, useState } from "react";

type AggregationLevel = "yearly" | "monthly" | "daily" | "session";

type TimelineItem = {
  timestamp: number;
  label: string;
  hasData: boolean;
  dataIndex?: number;
  isSelected?: boolean;
  date?: Date;
};

interface TimelineSliderProps {
  aggregationLevel: AggregationLevel;
  currentContext: Date;
  aggregatedData: any[];
  therapySessions?: any[];
  selectedIndex: number | null;
  onItemClick: (item: TimelineItem, index: number) => void;
  onWheelZoom?: (direction: "in" | "out") => void;
  onScrollTimeline?: (direction: "left" | "right") => void;
  onTimelineScroll?: (start: Date, end: Date) => void;
  selectedDay?: Date | null;
  selectedMonth?: number | null;
  dataDateRange?: { min: Date; max: Date };
  isZooming?: boolean;
  colors?: {
    primary?: string;
    border?: string;
    disabled?: string;
    text?: string;
    textDisabled?: string;
  };
}

export const TimelineSlider: React.FC<TimelineSliderProps> = ({
  aggregationLevel,
  currentContext,
  aggregatedData,
  therapySessions = [],
  selectedIndex,
  onItemClick,
  onWheelZoom,
  onScrollTimeline,
  onTimelineScroll,
  selectedDay,
  selectedMonth,
  dataDateRange,
  isZooming = false,
  colors: colorsProp,
}) => {
  const colors = {
    primary: colorsProp?.primary || "#1FB7E3",
    border: colorsProp?.border || "#e0e0e0",
    disabled: colorsProp?.disabled || "#f0f0f0",
    text: colorsProp?.text || "#666",
    textDisabled: colorsProp?.textDisabled || "#ccc",
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const wheelAccumulatorRef = useRef<number>(0);
  const isArrowScrollingRef = useRef<boolean>(false);
  const isTimelinePositioningRef = useRef<boolean>(false);
  const justZoomedRef = useRef<boolean>(false);
  const lastScrollTimeRef = useRef<number>(0);
  const scrollAccumulatorRef = useRef<number>(0);
  const touchStartRef = useRef<{ x: number; time: number } | null>(null);

  // NEW: Animation state for smooth swipe
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideOffset, setSlideOffset] = useState(0);

  const [visibleDaysCount, setVisibleDaysCount] = useState(15);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (isZooming) {
      justZoomedRef.current = true;
      console.log("🔒 Zoom started - blocking timeline sync");
    } else if (justZoomedRef.current) {
      setTimeout(() => {
        justZoomedRef.current = false;
        console.log("🔓 Zoom complete - timeline sync enabled");
      }, 500);
    }
  }, [isZooming]);

  useEffect(() => {
    const calculateVisibleDays = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const itemWidth = aggregationLevel === "yearly" ? 66 : 86;
      const arrowsWidth = 104;
      const availableWidth = containerWidth - arrowsWidth;

      const fitsCount = Math.floor(availableWidth / itemWidth);
      const dynamicCount = Math.max(3, Math.min(fitsCount, 21));

      setVisibleDaysCount(dynamicCount);
    };

    calculateVisibleDays();
    window.addEventListener("resize", calculateVisibleDays);

    return () => window.removeEventListener("resize", calculateVisibleDays);
  }, [aggregationLevel]);

  const timelineItems = useCallback((): TimelineItem[] => {
    console.log("🔄 Generating timeline items for:", aggregationLevel);

    if (aggregationLevel === "yearly") {
      const year = currentContext.getFullYear();
      const monthItems = Array.from({ length: 12 }, (_, i) => {
        const date = new Date(year, i, 1);
        const hasData = aggregatedData.some((d) => {
          const dataDate = new Date(d.timestamp);
          return dataDate.getFullYear() === year && dataDate.getMonth() === i;
        });

        const isSelected = selectedMonth !== null && selectedMonth === i;

        return {
          timestamp: date.getTime(),
          label: date.toLocaleString("en-US", { month: "short" }),
          hasData,
          dataIndex: i,
          isSelected: isSelected,
          date: date,
        };
      });

      console.log(
        "  Generated",
        monthItems.length,
        "months, selected:",
        selectedMonth
      );
      return monthItems;
    }

    if (
      aggregationLevel === "monthly" ||
      aggregationLevel === "daily" ||
      aggregationLevel === "session"
    ) {
      const items: TimelineItem[] = [];

      let startDate: Date;
      let endDate: Date;

      if (dataDateRange) {
        startDate = new Date(dataDateRange.min);
        endDate = new Date(dataDateRange.max);

        if (
          (aggregationLevel === "daily" || aggregationLevel === "session") &&
          selectedDay
        ) {
          if (selectedDay < startDate) {
            startDate = new Date(selectedDay);
          }
          if (selectedDay > endDate) {
            endDate = new Date(selectedDay);
          }
        }
      } else {
        startDate = new Date(currentContext);
        startDate.setDate(currentContext.getDate() - 30);
        endDate = new Date(currentContext);
        endDate.setDate(currentContext.getDate() + 30);
      }

      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const date = new Date(currentDate);

        const monthShort = date.toLocaleString("en-US", { month: "short" });
        const dayNum = date.getDate();

        const hasData = aggregatedData.some((d) => {
          const dDate = new Date(d.timestamp);
          return dDate.toDateString() === date.toDateString();
        });

        const isSelected =
          selectedDay && date.toDateString() === selectedDay.toDateString();

        items.push({
          timestamp: date.getTime(),
          label: `${monthShort} ${dayNum}`,
          hasData: hasData,
          isSelected: isSelected || false,
          date: date,
          dataIndex: items.length,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      console.log(
        "  Generated",
        items.length,
        "days, selected day:",
        selectedDay?.toDateString()
      );
      return items;
    }

    return [];
  }, [
    aggregationLevel,
    currentContext,
    aggregatedData,
    selectedDay,
    selectedMonth,
    dataDateRange,
  ]);

  const items = timelineItems();

  const getTimelineVisibleDateRange = useCallback((): {
    start: Date;
    end: Date;
  } | null => {
    if (!containerRef.current || items.length === 0) return null;

    const container = containerRef.current;
    const itemWidth = aggregationLevel === "yearly" ? 66 : 86;
    const scrollLeft = container.scrollLeft;
    const viewportWidth = container.offsetWidth - 104;

    const firstVisibleIndex = Math.floor(scrollLeft / itemWidth);
    const lastVisibleIndex = Math.min(
      items.length - 1,
      Math.ceil((scrollLeft + viewportWidth) / itemWidth)
    );

    const firstItem = items[firstVisibleIndex];
    const lastItem = items[lastVisibleIndex];

    if (!firstItem?.date || !lastItem?.date) return null;

    return {
      start: new Date(firstItem.date),
      end: new Date(lastItem.date),
    };
  }, [items, aggregationLevel]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (aggregationLevel !== "monthly") return;
    if (!onTimelineScroll) return;

    const container = containerRef.current;

    const handleScroll = () => {
      if (isTimelinePositioningRef.current) return;
      if (justZoomedRef.current) return;

      const visibleRange = getTimelineVisibleDateRange();
      if (!visibleRange) return;

      console.log(
        "📜 Monthly - Timeline → Viewport:",
        visibleRange.start.toDateString(),
        "to",
        visibleRange.end.toDateString()
      );

      onTimelineScroll(visibleRange.start, visibleRange.end);
    };

    let scrollTimeout: NodeJS.Timeout | null = null;
    const throttledScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    };

    container.addEventListener("scroll", throttledScroll);

    return () => {
      container.removeEventListener("scroll", throttledScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [aggregationLevel, onTimelineScroll, getTimelineVisibleDateRange]);

  useEffect(() => {
    if (!containerRef.current || isArrowScrollingRef.current) return;
    if (aggregationLevel !== "monthly") return;

    const container = containerRef.current;

    let targetIndex = -1;

    const targetDate = (selectedDay || currentContext).toDateString();
    targetIndex = items.findIndex(
      (item) => item.date && item.date.toDateString() === targetDate
    );

    if (targetIndex === -1) return;

    const itemWidth = timelineView === "yearly" ? 66 : 86;
    const containerWidth = container.offsetWidth;
    const arrowsWidth = 104;
    const viewportWidth = containerWidth - arrowsWidth;

    const currentScroll = container.scrollLeft;
    const targetItemLeft = targetIndex * itemWidth;
    const targetItemRight = targetItemLeft + itemWidth;
    const visibleLeft = currentScroll;
    const visibleRight = currentScroll + viewportWidth;

    const isOutsideView =
      targetItemLeft < visibleLeft || targetItemRight > visibleRight;

    if (isOutsideView) {
      console.log(
        "📍 Monthly view - Instantly centering selected item:",
        targetIndex
      );

      isTimelinePositioningRef.current = true;

      const originalBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = "auto";

      const halfViewport = viewportWidth / 2;
      const itemCenter = targetIndex * itemWidth + itemWidth / 2;
      const scrollLeft = Math.max(0, itemCenter - halfViewport);

      console.log("  Instant scroll to position:", scrollLeft);
      container.scrollLeft = scrollLeft;

      setTimeout(() => {
        container.style.scrollBehavior = originalBehavior;
        isTimelinePositioningRef.current = false;
      }, 100);
    }
  }, [currentContext, selectedDay, selectedMonth, items, aggregationLevel]);

  useEffect(() => {
    if (!containerRef.current || isArrowScrollingRef.current) return;
    if (aggregationLevel !== "daily" && aggregationLevel !== "session") return;
    if (!selectedDay) return;

    const container = containerRef.current;

    const targetDate = selectedDay.toDateString();
    const targetIndex = items.findIndex(
      (item) => item.date && item.date.toDateString() === targetDate
    );

    if (targetIndex === -1) return;

    const itemWidth = 86;
    const containerWidth = container.offsetWidth;
    const arrowsWidth = 104;
    const viewportWidth = containerWidth - arrowsWidth;

    const currentScroll = container.scrollLeft;
    const targetItemLeft = targetIndex * itemWidth;
    const targetItemRight = targetItemLeft + itemWidth;
    const visibleLeft = currentScroll;
    const visibleRight = currentScroll + viewportWidth;

    const isFullyVisible =
      targetItemLeft >= visibleLeft && targetItemRight <= visibleRight;

    if (!isFullyVisible) {
      console.log(
        "📍 Daily/Session - Scrolling to show selected date:",
        selectedDay.toDateString()
      );

      const originalBehavior = container.style.scrollBehavior;
      container.style.scrollBehavior = "auto";

      let scrollLeft: number;

      if (targetItemLeft < visibleLeft) {
        scrollLeft = targetItemLeft;
      } else {
        scrollLeft = targetItemRight - viewportWidth;
      }

      scrollLeft = Math.max(0, scrollLeft);

      console.log("  Scrolling to position:", scrollLeft);
      container.scrollLeft = scrollLeft;

      setTimeout(() => {
        container.style.scrollBehavior = originalBehavior;
      }, 50);
    }
  }, [selectedDay, items, aggregationLevel]);

  useEffect(() => {
    if (!onWheelZoom || !containerRef.current) return;

    const container = containerRef.current;
    const WHEEL_THRESHOLD = 100;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        wheelAccumulatorRef.current += e.deltaY;

        if (Math.abs(wheelAccumulatorRef.current) >= WHEEL_THRESHOLD) {
          const direction = wheelAccumulatorRef.current > 0 ? "out" : "in";
          onWheelZoom(direction);
          wheelAccumulatorRef.current = 0;
        }
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [onWheelZoom]);

  const timelineView = aggregationLevel === "yearly" ? "yearly" : "daily";

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const checkScroll = () => {
      if (aggregationLevel === "daily" || aggregationLevel === "session") {
        if (dataDateRange) {
          const currentDate = selectedDay || currentContext;
          const rangeStart = new Date(dataDateRange.min);
          const rangeEnd = new Date(dataDateRange.max);

          setCanScrollLeft(currentDate > rangeStart);
          setCanScrollRight(currentDate < rangeEnd);
        } else {
          setCanScrollLeft(true);
          setCanScrollRight(true);
        }
      } else {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        setCanScrollLeft(scrollLeft > 5);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
      }
    };

    checkScroll();
    const timer = setTimeout(checkScroll, 100);

    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      clearTimeout(timer);
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [items, aggregationLevel, selectedDay, currentContext, dataDateRange]);

  const handleArrowClick = useCallback(
    (direction: "left" | "right") => {
      if (!containerRef.current) return;

      const container = containerRef.current;

      if (aggregationLevel === "daily" || aggregationLevel === "session") {
        const currentDate = selectedDay || currentContext;
        const newDate = new Date(currentDate);

        if (direction === "left") {
          newDate.setDate(newDate.getDate() - 1);
        } else {
          newDate.setDate(newDate.getDate() + 1);
        }

        if (dataDateRange) {
          const rangeStart = new Date(dataDateRange.min);
          const rangeEnd = new Date(dataDateRange.max);

          if (newDate < rangeStart || newDate > rangeEnd) {
            console.log("🚫 Cannot navigate beyond data range");
            return;
          }
        }

        console.log(
          "📅 Arrow navigation:",
          direction,
          "→",
          newDate.toDateString()
        );

        const newIndex = items.findIndex(
          (item) =>
            item.date && item.date.toDateString() === newDate.toDateString()
        );

        if (newIndex !== -1) {
          onItemClick(items[newIndex], newIndex);
        } else {
          console.warn("Date not found in timeline:", newDate.toDateString());
        }

        return;
      }

      const itemWidth = aggregationLevel === "yearly" ? 66 : 86;
      const scrollAmount = itemWidth * 3;

      isArrowScrollingRef.current = true;
      container.style.scrollBehavior = "smooth";

      if (direction === "left") {
        container.scrollLeft -= scrollAmount;
      } else {
        container.scrollLeft += scrollAmount;
      }

      setTimeout(() => {
        isArrowScrollingRef.current = false;
        container.style.scrollBehavior = "auto";
      }, 300);

      console.log("Arrow scroll:", direction, "by", scrollAmount, "px");
    },
    [
      aggregationLevel,
      selectedDay,
      currentContext,
      dataDateRange,
      items,
      onItemClick,
    ]
  );

  // NEW: Enhanced swipe/scroll handler with smooth slide animation
  const handleSwipeOrScroll = useCallback(
    (direction: "left" | "right") => {
      // Prevent animation if already animating
      if (isAnimating) {
        console.log("⏸️ Animation in progress, ignoring swipe");
        return;
      }

      // Check boundaries
      if (direction === "left" && !canScrollLeft) {
        console.log("🚫 At left boundary, no animation");
        return;
      }
      if (direction === "right" && !canScrollRight) {
        console.log("🚫 At right boundary, no animation");
        return;
      }

      console.log("🎬 Starting slide animation:", direction);

      // 1. Start animation - set offset for visual slide
      setIsAnimating(true);
      const itemWidth = 86;
      const offset = direction === "left" ? itemWidth : -itemWidth;
      setSlideOffset(offset);

      // 2. Immediately update selection (so new date highlights)
      handleArrowClick(direction);

      // 3. Animate back to 0 (creates the slide effect)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSlideOffset(0);

          // 4. Clear animation flag after transition completes
          setTimeout(() => {
            setIsAnimating(false);
            console.log("✅ Slide animation complete");
          }, 200); // Match CSS transition duration
        });
      });
    },
    [isAnimating, canScrollLeft, canScrollRight, handleArrowClick]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    if (aggregationLevel !== "daily" && aggregationLevel !== "session") return;

    const container = containerRef.current;
    const SCROLL_COOLDOWN = 300;
    const SCROLL_THRESHOLD = 20;
    const SWIPE_THRESHOLD = 30;
    const SWIPE_TIME_MAX = 300;

    // Wheel scroll handler - NOW with animation
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Skip if animating
      if (isAnimating) return;

      const now = Date.now();
      if (now - lastScrollTimeRef.current < SCROLL_COOLDOWN) return;

      scrollAccumulatorRef.current += e.deltaY + e.deltaX;

      if (Math.abs(scrollAccumulatorRef.current) > SCROLL_THRESHOLD) {
        const direction = scrollAccumulatorRef.current > 0 ? "right" : "left";

        console.log("🖱️ Wheel scroll detected:", direction);
        handleSwipeOrScroll(direction); // Use animated handler

        lastScrollTimeRef.current = now;
        scrollAccumulatorRef.current = 0;
      }
    };

    // Touch start handler
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        touchStartRef.current = {
          x: e.touches[0].clientX,
          time: Date.now(),
        };
      }
    };

    // Touch move handler
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Touch end handler - NOW with animation
    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current || e.changedTouches.length === 0) return;

      const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
      const deltaTime = Date.now() - touchStartRef.current.time;

      if (Math.abs(deltaX) > SWIPE_THRESHOLD && deltaTime < SWIPE_TIME_MAX) {
        // Skip if animating
        if (isAnimating) {
          touchStartRef.current = null;
          return;
        }

        const now = Date.now();
        if (now - lastScrollTimeRef.current < SCROLL_COOLDOWN) {
          touchStartRef.current = null;
          return;
        }

        const direction = deltaX > 0 ? "left" : "right";

        console.log("👆 Touch swipe detected:", direction, "deltaX:", deltaX);
        handleSwipeOrScroll(direction); // Use animated handler

        lastScrollTimeRef.current = now;
      }

      touchStartRef.current = null;
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [aggregationLevel, handleSwipeOrScroll, isAnimating]);

  return (
    <div
      style={{
        height: "60px",
        background: "rgba(248, 250, 252, 0.95)",
        backdropFilter: "blur(12px)",
        borderRadius: "16px",
        boxShadow:
          "0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
        display: "flex",
        alignItems: "center",
        padding: "0 12px",
        gap: "12px",
        position: "relative",
      }}
    >
      {/* Left Arrow */}
      <button
        onClick={() => handleArrowClick("left")}
        disabled={!canScrollLeft}
        aria-label="Scroll left"
        style={{
          width: "40px",
          height: "40px",
          minWidth: "40px",
          borderRadius: "8px",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          background: "white",
          color: !canScrollLeft ? "#ccc" : "#666",
          cursor: !canScrollLeft ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          flexShrink: 0,
          zIndex: 2,
          opacity: !canScrollLeft ? 0.4 : 1,
          boxShadow: !canScrollLeft ? "none" : "0 2px 4px rgba(0, 0, 0, 0.05)",
        }}
        onMouseEnter={(e) => {
          if (canScrollLeft) {
            e.currentTarget.style.background = "#f8f9fa";
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.color = colors.primary;
            e.currentTarget.style.transform = "scale(1.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (canScrollLeft) {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
            e.currentTarget.style.color = "#666";
            e.currentTarget.style.transform = "scale(1)";
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 12L6 8l4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Timeline Items Container - NEW: With transform animation */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: "flex",
          gap: "6px",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          position: "relative",
          padding: "4px 0",
          // NEW: Transform for slide animation
          transform: `translateX(${slideOffset}px)`,
          // NEW: Transition when sliding back to 0
          transition: slideOffset === 0 ? "transform 0.2s ease-out" : "none",
          // NEW: Lock pointer events during animation
          pointerEvents: isAnimating ? "none" : "auto",
        }}
        className="timeline-scroll"
      >
        <style>
          {`
            .timeline-scroll::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        {items.map((item, index) => {
          const isActive = item.isSelected;
          const isDisabled = !item.hasData;

          return (
            <button
              key={item.timestamp}
              ref={isActive ? selectedRef : null}
              onClick={() => onItemClick(item, index)}
              disabled={false}
              style={{
                minWidth: timelineView === "yearly" ? "60px" : "80px",
                height: "44px",
                padding: "0 16px",
                borderRadius: "10px",
                border: isActive
                  ? `2px solid ${colors.primary}`
                  : isDisabled
                  ? "1px solid rgba(0, 0, 0, 0.06)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
                background: isActive
                  ? colors.primary
                  : isDisabled
                  ? "rgba(0, 0, 0, 0.02)"
                  : "white",
                fontSize: "13px",
                fontWeight: isActive ? "600" : isDisabled ? "400" : "500",
                color: isActive ? "white" : isDisabled ? "#bbb" : "#444",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                opacity: 1,
                boxShadow: isActive
                  ? `0 4px 16px ${colors.primary}40, 0 0 0 4px ${colors.primary}15`
                  : isDisabled
                  ? "none"
                  : "0 2px 4px rgba(0, 0, 0, 0.04)",
                transform: isActive ? "scale(1.1)" : "scale(1)",
                letterSpacing: "0.3px",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = isDisabled
                    ? "rgba(0, 0, 0, 0.04)"
                    : "#f8f9fa";
                  e.currentTarget.style.borderColor = isDisabled
                    ? "rgba(0, 0, 0, 0.08)"
                    : colors.primary;
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow = isDisabled
                    ? "none"
                    : `0 4px 8px rgba(0, 0, 0, 0.08)`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = isDisabled
                    ? "rgba(0, 0, 0, 0.02)"
                    : "white";
                  e.currentTarget.style.borderColor = isDisabled
                    ? "rgba(0, 0, 0, 0.06)"
                    : "rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = isDisabled
                    ? "none"
                    : "0 2px 4px rgba(0, 0, 0, 0.04)";
                }
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Right Arrow */}
      <button
        onClick={() => handleArrowClick("right")}
        disabled={!canScrollRight}
        aria-label="Scroll right"
        style={{
          width: "40px",
          height: "40px",
          minWidth: "40px",
          borderRadius: "8px",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          background: "white",
          color: !canScrollRight ? "#ccc" : "#666",
          cursor: !canScrollRight ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          flexShrink: 0,
          zIndex: 2,
          opacity: !canScrollRight ? 0.4 : 1,
          boxShadow: !canScrollRight ? "none" : "0 2px 4px rgba(0, 0, 0, 0.05)",
        }}
        onMouseEnter={(e) => {
          if (canScrollRight) {
            e.currentTarget.style.background = "#f8f9fa";
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.color = colors.primary;
            e.currentTarget.style.transform = "scale(1.05)";
          }
        }}
        onMouseLeave={(e) => {
          if (canScrollRight) {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
            e.currentTarget.style.color = "#666";
            e.currentTarget.style.transform = "scale(1)";
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 12l4-4-4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default TimelineSlider;
